<template>
  <div class="content-main relative">
    <BaseOrderModal
      @closeOrder="openedOrder = ''"
      @showDraft="showDraft"
      :orderUUID="openedOrder"
      @openParentOrder="openOrder"
      @openChildOrder="openOrder"
      @quoteConverted="openOrder"
      @nextOrder="openNextOrder"
      @previousOrder="openPreviousOrder"
      v-model="showOrderModal"
      @orderUpdated="onOrderUpdated"
      :hasNext="hasNext"
      :hasPrev="hasPrev"
    />

    <!-- Mobile Menu Modal -->
    <div
      v-if="showMobileMenu"
      class="mobile-menu-overlay"
      @click="showMobileMenu = false"
    >
      <div class="mobile-menu-content" @click.stop>
        <div class="mobile-menu-header">
          <h3>{{ $t("message.orders_page.filter_by_status") }}</h3>
          <button @click="showMobileMenu = false" class="close-btn">
            <i class="mdi mdi-close"></i>
          </button>
        </div>
        <BaseLeftPageSubMenu
          :menuItems="orderStatusOptions"
          :selectedStatus="filters.statusShipment"
          :hideToggle="true"
          :searchEnabled="false"
          :collapsible="false"
          @selectedStatusFilter="updateStatusFilterMobile"
        >
        </BaseLeftPageSubMenu>
      </div>
    </div>

    <!-- Desktop Sidebar: ocupa lo que mida el menu (255px abierto, la franja
         de 24px plegado), asi el contenido crece cuando se oculta. -->
    <div class="desktop-sidebar">
      <BaseLeftPageSubMenu
        :menuItems="orderStatusOptions"
        :selectedStatus="filters.statusShipment"
        :hideToggle="true"
        :searchEnabled="false"
        @selectedStatusFilter="updateStatusFilter"
      >
      </BaseLeftPageSubMenu>
    </div>

    <!-- Las cotizaciones viven en esta vista: el vendedor arranca con un quote
         y de ahí sale el draft al convertirlo. Tienen su propia lista
         (/quotes/list), así que no pasan por los filtros de órdenes. -->
    <div v-if="isQuotesSection" class="content full-width">
      <QuotesList ref="quotesList" @openQuote="openOrder" />
    </div>

    <template v-else>
      <div class="content full-width">
        <!-- Search Bar -->
        <div class="search-bar">
          <div class="search-wrapper">
            <i class="mdi mdi-magnify search-icon"></i>
            <input
              type="text"
              v-model="filters.search"
              placeholder="Search by order #, name, address, phone, postal code or product..."
              class="search-input"
              @keyup.enter="applyFilters"
            />
            <button
              v-if="filters.search"
              @click="filters.search = ''"
              class="clear-search"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>
          <div class="search-actions">
            <button
              @click="applyFilters"
              class="btn-dark-button"
              :disabled="!hasFilterChanges"
            >
              {{ $t("message.orders_page.apply") }}
            </button>
            <button
              @click="resetFilters"
              class="btn-clear-button ml-2"
              v-if="hasFilterChanges"
            >
              {{ $t("message.orders_page.reset") }}
            </button>
          </div>
          <!-- Fuente de la búsqueda: MySQL (/orders/list) o Typesense, para
               comparar resultados antes de decidir. -->
          <!-- Sólo donde hay key de órdenes (hoy, local). Tras añadirla al
               .env hay que reiniciar npm run serve: Vue la lee al arrancar. -->
          <label
            v-if="typesenseOrdersAvailable"
            class="source-switch"
            title="Buscar en Typesense en vez de MySQL"
          >
            <input
              type="checkbox"
              :checked="source === 'typesense'"
              @change="source = $event.target.checked ? 'typesense' : 'mysql'"
            />
            <span class="switch-track"><span class="switch-thumb"></span></span>
            <span class="switch-label">Typesense</span>
          </label>
          <div
            v-if="searchStats || searchError"
            class="search-stats"
            :class="{ 'is-error': searchError }"
          >
            {{ searchError || searchStats }}
          </div>
        </div>

        <div class="header flex flex-row">
          <BaseMiniSpinner v-if="loading" class="mr-2" />
          <div class="header-title">
            {{ $t("message.orders_page.title") }}
            {{
              filters.statusShipment
                ? orderStatusOptions.find(
                    option => option.value === filters.statusShipment
                  )?.originalDisplayText || ""
                : "(" +
                  $t("message.orders_page.order_statuses.all_status") +
                  ")"
            }}
          </div>
          <div class="header-actions">
            <div class="mobile-actions">
              <!-- Mobile menu button -->
              <button
                @click="showMobileMenu = true"
                class="mobile-menu-btn lg:hidden"
                :title="$t('message.orders_page.menu')"
              >
                <i class="mdi mdi-menu text-xl"></i>
              </button>
              <!-- Additional filters button -->
              <button
                @click="showFilters = !showFilters"
                class="filter-btn"
                :class="{
                  'is-active': showFilters || appliedOtherFiltersCount
                }"
                :title="$t('message.orders_page.additional_filters')"
              >
                <i class="mdi mdi-filter-variant text-xl"></i>
                <span class="filter-btn-label">Filters</span>
                <span v-if="appliedOtherFiltersCount" class="filter-btn-count">
                  {{ appliedOtherFiltersCount }}
                </span>
              </button>
              <!-- Exporta lo mismo que muestra la lista (filtros aplicados) -->
              <button
                v-if="userCanExportOrders"
                @click="exportCsv"
                class="filter-btn export-btn"
                :disabled="exporting || loading || !totalItems"
                :title="`Export ${totalItems} orders to CSV`"
              >
                <BaseMiniSpinner v-if="exporting" />
                <i v-else class="mdi mdi-file-download-outline text-xl"></i>
                <span class="filter-btn-label">CSV</span>
              </button>
            </div>
            <!-- Ya no se crean drafts sueltos: un draft sale siempre de
                 convertir una cotización. -->
            <button
              v-if="userCanViewQuotes"
              class="btn-dark-button new-order-btn"
              @click="newQuote()"
            >
              {{ $t("message.quotes_page.new_quote") }}
            </button>
          </div>
        </div>
        <OrdersFilters
          v-if="showFilters"
          :showFilters="showFilters"
          @closed="showFilters = false"
          v-model="filters.otherFilters"
          @clearFilters="clearFilters"
          @apply="applyOtherFilters"
          :key="filtersKey"
        ></OrdersFilters>
        <!-- Active filter badges -->
        <div
          v-if="!showFilters && activeFilterBadges.length"
          class="active-filters-bar"
        >
          <span
            v-for="badge in activeFilterBadges"
            :key="badge.key"
            class="filter-badge"
          >
            {{ badge.label }}
            <button
              class="filter-badge-remove"
              @click="removeFilter(badge.key)"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </span>
        </div>

        <div v-if="filters.statusShipment === 'dropship'">
          <!-- buttons for processed and not processed -->
          <button
            class="btn-info-button text-black"
            :class="{
              'border-4 border-red-500':
                filters.otherFilters.dropshipProcessed == 0
            }"
            @click="filters.otherFilters.dropshipProcessed = 0"
          >
            {{ $t("message.orders_page.not_processed") }}
          </button>
          <button
            class="btn-dark-button bg-green-500 text-black"
            :class="{
              'border-4 border-red-500':
                filters.otherFilters.dropshipProcessed == 1
            }"
            @click="filters.otherFilters.dropshipProcessed = 1"
          >
            {{ $t("message.orders_page.processed") }}
          </button>
        </div>
        <!-- Desktop Table View -->
        <div class="table desktop-table">
          <table class="default-table">
            <thead>
              <tr class="table100-head">
                <th
                  v-for="headerItem in tableHeaders"
                  :key="headerItem.value"
                  @click="changeArrowDir(headerItem.value)"
                >
                  {{ headerItem.displayText }}
                  <span v-if="filters.orderCol == headerItem.value">
                    <img
                      :src="
                        require(`@/assets/icons/${arrowDirection}-arrow.svg`)
                      "
                    />
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in orders"
                :key="order.orderNumber"
                @click="openOrder(order.orderNumber)"
                :class="{
                  selected: selectedOrder == order.id,
                  processed: order.dropshipProcessed == 1
                }"
              >
                <td>
                  <div class="flex items-center gap-2">
                    <a>
                      {{ order.orderNumber }}
                      {{
                        order.parentOrderNumber &&
                        order.parentOrderNumber !== order.orderNumber
                          ? ` (${$t("message.orders_page.parent")} ${
                              order.parentOrderNumber
                            })`
                          : ""
                      }}
                    </a>
                    <div
                      v-if="order.statusPayment == 0"
                      class="payment-warning-badge"
                      :title="$t('message.orders_page.not_paid')"
                    >
                      <i class="mdi mdi-alert-circle"></i>
                    </div>
                  </div>
                </td>
                <td>
                  {{ order.orderDate | dateWithHours }}
                </td>
                <td>
                  {{ translateStatus(order.status) }}
                </td>
                <td>
                  <BaseTooltip
                    :text="order.products"
                    :disabled="!order.products || order.products == ''"
                  >
                    {{ order.products }}
                  </BaseTooltip>
                </td>
                <td>
                  <BaseTooltip
                    :text="order.shippingAddressName"
                    :disabled="
                      !order.shippingAddressName ||
                        order.shippingAddressName == ''
                    "
                  >
                    {{ order.shippingAddressName }}
                  </BaseTooltip>
                </td>
                <td>
                  {{ order.shippingAddressState }}
                </td>
                <td>
                  {{ order.shippingAddressCountry }}
                </td>
                <td>
                  {{ order.createdBy }}
                </td>
                <td>{{ order.total | currencyFormatted(true) }}</td>
                <td>
                  {{ order.shippingPreference }}
                </td>
                <td>
                  <div
                    v-if="order.vin !== '' && order.vin !== null"
                    class="w-5 h-5 bg-green-500 rounded-full border-2 border-black"
                  ></div>
                </td>
                <td>{{ order.sellerName }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty state -->
        <div v-if="!loading && orders.length === 0" class="no-results">
          <i class="mdi mdi-magnify-close no-results-icon"></i>
          <template v-if="appliedFilters.search">
            <p>
              No orders match
              <strong>"{{ appliedFilters.search }}"</strong>
            </p>
            <button @click="removeFilter('search')" class="btn-clear-button">
              {{ $t("message.orders_page.reset") }}
            </button>
          </template>
          <template v-else>
            <p>No orders match the current filters</p>
          </template>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards">
          <div
            v-for="order in orders"
            :key="order.orderNumber"
            @click="openOrder(order.orderNumber)"
            class="order-card"
            :class="{
              selected: selectedOrder == order.id,
              warning: order.statusPayment == 0,
              processed: order.dropshipProcessed == 1
            }"
          >
            <div class="card-header">
              <div class="order-number">
                <i class="mdi mdi-receipt text-blue-600"></i>
                <span class="font-bold">
                  {{ order.orderNumber }}
                  <span
                    v-if="
                      order.parentOrderNumber &&
                        order.parentOrderNumber !== order.orderNumber
                    "
                    class="text-xs text-gray-500"
                  >
                    ({{ $t("message.orders_page.parent") }}:
                    {{ order.parentOrderNumber }})
                  </span>
                </span>
              </div>
              <div class="order-total">
                <span class="font-bold text-green-600">{{
                  order.total | currencyFormatted(true)
                }}</span>
              </div>
            </div>

            <div class="card-content">
              <div class="info-row">
                <i class="mdi mdi-calendar text-gray-500"></i>
                <span>{{ order.orderDate | dateWithHours }}</span>
              </div>

              <div class="info-row" v-if="order.status">
                <i class="mdi mdi-information text-gray-500"></i>
                <span
                  >{{ $t("message.orders_page.status") }}:
                  {{ translateStatus(order.status) }}</span
                >
              </div>

              <div class="info-row" v-if="order.products">
                <i class="mdi mdi-package-variant text-gray-500"></i>
                <span class="truncate">{{ order.products }}</span>
              </div>

              <div class="info-row" v-if="order.shippingAddressName">
                <i class="mdi mdi-account text-gray-500"></i>
                <span class="truncate">{{ order.shippingAddressName }}</span>
              </div>

              <div
                class="info-row"
                v-if="
                  order.shippingAddressState || order.shippingAddressCountry
                "
              >
                <i class="mdi mdi-map-marker text-gray-500"></i>
                <span
                  >{{ order.shippingAddressState }},
                  {{ order.shippingAddressCountry }}</span
                >
              </div>

              <div class="info-row" v-if="order.createdBy">
                <i class="mdi mdi-account-circle text-gray-500"></i>
                <span
                  >{{ $t("message.orders_page.created_by") }}:
                  {{ order.createdBy }}</span
                >
              </div>

              <div class="info-row" v-if="order.shippingPreference">
                <i class="mdi mdi-truck text-gray-500"></i>
                <span>{{ order.shippingPreference }}</span>
              </div>

              <div class="info-row" v-if="order.sellerName">
                <i class="mdi mdi-account-tie text-gray-500"></i>
                <span
                  >{{ $t("message.orders_page.seller") }}:
                  {{ order.sellerName }}</span
                >
              </div>
            </div>

            <div class="card-footer">
              <div class="status-indicators">
                <div
                  v-if="order.vin !== '' && order.vin !== null"
                  class="vin-indicator"
                  :title="$t('message.orders_page.has_vin')"
                >
                  <i class="mdi mdi-car text-green-600"></i>
                </div>
                <div
                  v-if="order.statusPayment == 0"
                  class="payment-warning"
                  :title="$t('message.orders_page.payment_issue')"
                >
                  <i class="mdi mdi-alert-circle text-orange-500"></i>
                </div>
                <div
                  v-if="order.dropshipProcessed == 1"
                  class="processed-indicator"
                  :title="$t('message.orders_page.processed')"
                >
                  <i class="mdi mdi-check-circle text-green-600"></i>
                </div>
              </div>
              <div class="card-action">
                <i class="mdi mdi-chevron-right text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>
        <!-- Pie de la tabla: va pegado a la ultima fila y, en listas largas,
             queda fijo abajo mientras se hace scroll -->
        <div v-if="orders.length" class="table-footer">
          <div class="totals">
            {{ $t("message.orders_page.showing") }}
            <strong>{{ showingFrom() }}</strong>
            {{ $t("message.orders_page.to") }}
            <strong>{{ toQuantity() }}</strong>
            {{ $t("message.orders_page.from") }}
            <strong>{{ totalItems }}</strong>
            {{ $t("message.orders_page.results") }}
          </div>
          <div class="footer-controls">
            <label class="per-page">
              <select
                v-model.number="ordersByPage"
                @change="
                  (appliedFilters.limitPerPage = ordersByPage),
                    (appliedFilters.page = 1),
                    (filters.limitPerPage = ordersByPage),
                    (filters.page = 1)
                "
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
              {{ $t("message.orders_page.per_page") }}
            </label>
            <div class="pager">
              <button
                class="pager-btn"
                :disabled="appliedFilters.page <= 1"
                @click="previous()"
              >
                <i class="mdi mdi-chevron-left"></i>
                <span>{{ $t("message.orders_page.previous") }}</span>
              </button>
              <span class="pager-page">
                {{ $t("message.orders_page.page") }}
                {{ appliedFilters.page }} / {{ totalPages }}
              </span>
              <button
                class="pager-btn"
                :disabled="appliedFilters.page >= totalPages"
                @click="next()"
              >
                <span>{{ $t("message.orders_page.next") }}</span>
                <i class="mdi mdi-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  // OrdersPage2 (served at /orders) is backed by the fast orders_v2-only
  // endpoint (Orders::list_get) instead of the fully-joined /orders/set.
  // Every filter in the "Additional Filters" panel is applied server-side, and
  // /orders/export_csv reuses exactly the same filters.
  getOrdersFromIndex as getOrders,
  createQueryString,
  exportOrdersCsv,
  getOrdersQtyByStatus
} from "@/api/store/OrdersService.js";
import OrdersFilters from "@/components/order/OrdersFilters.vue";
import QuotesList from "@/components/quotes/QuotesList.vue";
import { getSelectedDepartmentCode } from "@/api/httpStore.js";
import {
  searchOrders as searchOrdersTypesense,
  checkTypesenseOrdersAvailable
} from "@/utils/typesenseOrders.js";
import { orderStatusMap, getOrderStatusKey } from "@/constants/orderStatusMap";
// Por defecto se busca en Typesense; si el usuario apaga el switch, MySQL se
// recuerda en este navegador hasta que lo vuelva a encender. Sin localStorage
// (modo privado, bloqueado) queda en Typesense.
const SOURCE_STORAGE_KEY = "orders.searchSource";
// Pausa tras la última tecla antes de buscar en Typesense.
const SEARCH_AS_YOU_TYPE_MS = 250;
const readSavedSource = () => {
  try {
    return window.localStorage.getItem(SOURCE_STORAGE_KEY);
  } catch {
    return null;
  }
};
const saveSource = source => {
  try {
    if (source === "mysql") {
      window.localStorage.setItem(SOURCE_STORAGE_KEY, source);
    } else {
      window.localStorage.removeItem(SOURCE_STORAGE_KEY);
    }
  } catch {
    // Sin almacenamiento sólo se pierde el recuerdo, no la búsqueda.
  }
};
const baseFilters = {
  statusShipment: "awaiting_shipment",
  otherFilters: {
    createdBy: "",
    seller: "",
    fromDate: "",
    toDate: "",
    paidStatus: "",
    creditRetail: "",
    hideCancelled: true,
    dropshipProcessed: 0,
    customerInfoautoId: "",
    nkrCode: "",
    shippingAddressName: "",
    totalMin: "",
    totalMax: ""
  },
  page: 1,
  limitPerPage: 20,
  orderCol: "orderDate",
  orderDir: "desc",
  search: ""
};
// Entrada del menú lateral que no es un estado de orden sino las cotizaciones
const QUOTES_SECTION = "quotes";
export default {
  name: "OrdersPage2",
  components: { OrdersFilters, QuotesList },

  data() {
    return {
      publicPath: process.env.VUE_APP_IMG_BASE_URL,
      filters: JSON.parse(JSON.stringify(baseFilters)),
      appliedFilters: JSON.parse(JSON.stringify(baseFilters)),
      filtersKey: 0,
      ordersByPage: 20,
      orders: [],
      totalItems: 0,
      totalPages: 0,
      loading: true,
      selectedOrder: null,
      openedOrder: "",
      showOrderModal: false,
      showFilters: false,
      showMobileMenu: false,
      apiCallId: null,
      hasNext: false,
      hasPrev: false,
      windowIsFocused: true,
      generalStatus: null,
      exporting: false,
      // Se decide en mounted, cuando se sabe si la tienda tiene búsqueda de
      // órdenes en Typesense; hasta entonces el watcher de source no actúa.
      source: "mysql",
      sourceReady: false,
      typesenseOrdersAvailable: false,
      searchStats: "",
      searchError: ""
    };
  },
  watch: {
    appliedFilters: {
      deep: true,
      handler() {
        this.loading = true;
        const query = {
          ...this.$route.query,
          search: createQueryString(this.appliedFilters)
        };
        this.$router.replace({ query }).catch(() => {});
        this.loadOrders();
      }
    },
    // Con Typesense se busca mientras se escribe: responde en milisegundos.
    // Con MySQL sigue haciendo falta Enter/Apply, que cada búsqueda es cara.
    // Sólo aplica el texto: otros filtros a medio editar esperan a Apply.
    "filters.search"() {
      if (this.source !== "typesense") return;
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        if (this.filters.search === this.appliedFilters.search) return;
        this.filters.page = 1;
        this.appliedFilters = {
          ...JSON.parse(JSON.stringify(this.appliedFilters)),
          search: this.filters.search,
          page: 1
        };
      }, SEARCH_AS_YOU_TYPE_MS);
    },
    source() {
      if (!this.sourceReady) return;
      saveSource(this.source);
      this.loading = true;
      this.loadOrders(true);
    },
    // El selector de departamento navega a la misma ruta y el componente se
    // reutiliza, así que mounted no vuelve a cargar. La primera asignación
    // (init del layout) ya la cubre mounted.
    activeDepartmentCode(newCode, oldCode) {
      if (!oldCode || newCode === oldCode) return;
      this.loading = true;
      this.loadOrders(true);
      this.getStatus();
    },
    showFilters() {
      const query = {
        ...this.$route.query,
        showFilters: this.showFilters
      };
      this.$router.replace({ query }).catch(() => {});
    }
  },
  computed: {
    activeDepartmentCode() {
      return this.$store.getters["general/getActiveDepartment"]?.code;
    },
    getAdminSettings() {
      return this.$store.getters["general/getAdminSettings"];
    },
    userCanExportOrders() {
      return (
        this.$store.getters["auth/currentUser"]?.role === "superadmin" ||
        this.$store.getters["auth/userHasPermission"]("export_orders")
      );
    },
    userCanViewQuotes() {
      return this.$store.getters["auth/userHasPermission"]("can_view_quotes");
    },
    isQuotesSection() {
      return this.appliedFilters.statusShipment === QUOTES_SECTION;
    },
    currency() {
      const currencyObj = this.getAdminSettings.get("currency");
      return currencyObj?.value || "$";
    },
    arrowDirection() {
      return this.filters.orderDir == "desc" ? "down" : "up";
    },
    hasFilterChanges() {
      return (
        JSON.stringify(this.filters) !== JSON.stringify(this.appliedFilters)
      );
    },
    appliedOtherFiltersCount() {
      return this.activeFilterBadges.filter(badge => badge.key !== "search")
        .length;
    },
    activeFilterBadges() {
      const f = this.appliedFilters?.otherFilters;
      if (!f) return [];
      const badges = [];
      const paidLabels = { "1": "Paid", "0": "Pending payment" };
      const creditLabels = { "0": "Credit", "1": "Retail" };
      const createdByLabels = { user: "User", seller: "Admin-Seller" };
      if (this.appliedFilters?.search)
        badges.push({
          key: "search",
          label: `Search: ${this.appliedFilters.search}`
        });
      if (f.customerInfoautoId)
        badges.push({
          key: "customerInfoautoId",
          label: `Customer: ${f.customerInfoautoId}`
        });
      if (f.seller) {
        const seller = (this.$store.getters["general/getSellers"] || []).find(
          s => s.email === f.seller
        );
        badges.push({
          key: "seller",
          label: `Seller: ${seller ? seller.name : f.seller}`
        });
      }
      if (f.createdBy)
        badges.push({
          key: "createdBy",
          label: `Created by: ${createdByLabels[f.createdBy] || f.createdBy}`
        });
      if (f.fromDate)
        badges.push({ key: "fromDate", label: `From: ${f.fromDate}` });
      if (f.toDate) badges.push({ key: "toDate", label: `To: ${f.toDate}` });
      if (
        !f.hideCancelled &&
        this.appliedFilters.statusShipment !== "cancelled"
      )
        badges.push({ key: "hideCancelled", label: "Including cancelled" });
      if (
        f.paidStatus !== "" &&
        f.paidStatus !== null &&
        f.paidStatus !== undefined
      )
        badges.push({
          key: "paidStatus",
          label: `Payment: ${paidLabels[f.paidStatus] || f.paidStatus}`
        });
      if (
        f.creditRetail !== "" &&
        f.creditRetail !== null &&
        f.creditRetail !== undefined
      )
        badges.push({
          key: "creditRetail",
          label: `Type: ${creditLabels[f.creditRetail] || f.creditRetail}`
        });
      if (f.shippingAddressName)
        badges.push({
          key: "shippingAddressName",
          label: `Ship name: ${f.shippingAddressName}`
        });
      if (f.totalMin)
        badges.push({ key: "totalMin", label: `Min $${f.totalMin}` });
      if (f.totalMax)
        badges.push({ key: "totalMax", label: `Max $${f.totalMax}` });
      if (f.nkrCode)
        badges.push({ key: "nkrCode", label: `Product: ${f.nkrCode}` });
      return badges;
    },
    tableHeaders() {
      return [
        {
          displayText: this.$t(
            "message.orders_page.table_headers.order_number"
          ),
          value: "orderNumber"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.order_date"),
          value: "orderDate"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.status"),
          value: "status"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.products"),
          value: "products"
        },
        {
          displayText: this.$t(
            "message.orders_page.table_headers.shipping_name"
          ),
          value: "shippingName"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.state"),
          value: "state"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.country"),
          value: "country"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.created_by"),
          value: "createdBy"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.total"),
          value: "total"
        },
        {
          displayText: this.$t(
            "message.orders_page.table_headers.shipping_preference"
          ),
          value: "shippingPreference"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.vin"),
          value: "vin"
        },
        {
          displayText: this.$t("message.orders_page.table_headers.seller"),
          value: "marketplace"
        }
      ];
    },
    orderStatusOptions() {
      // Junto a draft: un draft sale siempre de convertir una cotización
      const quotesOptions = this.userCanViewQuotes
        ? [
            {
              displayText: this.$t("message.quotes_page.quotes_tab"),
              originalDisplayText: this.$t("message.quotes_page.quotes_tab"),
              value: QUOTES_SECTION
            }
          ]
        : [];
      return [
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.awaiting_shipment"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.awaiting_shipment"
          ),
          count: this.getStatusCount("awaiting_shipment"),
          value: "awaiting_shipment"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.awaiting_payment"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.awaiting_payment"
          ),
          count: this.getStatusCount("awaiting_payment"),
          value: "awaiting_payment"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.shipped"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.shipped"
          ),
          count: this.getStatusCount("shipped"),
          value: "shipped"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.cancel_request_pending"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.cancel_request_pending"
          ),
          count: this.getStatusCount("cancel_request_pending"),
          value: "Cancel request pending"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.cancelled"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.cancelled"
          ),
          count: this.getStatusCount("cancelled"),
          value: "cancelled"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.on_hold"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.on_hold"
          ),
          count: this.getStatusCount("on_hold"),
          value: "on_hold"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.local_dropship"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.local_dropship"
          ),
          count: this.getStatusCount("local_dropship"),
          value: "Local Dropship"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.dropship_pending"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.dropship_pending"
          ),
          count: this.getStatusCount("dropship_pending"),
          value: "Dropship Pending"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.dropship_awaiting_shipment"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.dropship_awaiting_shipment"
          ),
          count: this.getStatusCount("dropship_processed"),
          value: "Dropship processed"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.vin_verification"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.vin_verification"
          ),
          count: this.getStatusCount("vin_verification"),
          value: "VIN_verification"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.processing"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.processing"
          ),
          count: this.getStatusCount("processing"),
          value: "processing"
        },
        {
          displayText: this.$t("message.orders_page.order_statuses.all_status"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.all_status"
          ),
          value: ""
        },
        ...quotesOptions,
        {
          displayText: this.$t("message.orders_page.order_statuses.draft"),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.draft"
          ),
          count: this.getStatusCount("draft"),
          value: "draft"
        },
        {
          displayText: this.$t(
            "message.orders_page.order_statuses.warehouse_allocation"
          ),
          originalDisplayText: this.$t(
            "message.orders_page.order_statuses.warehouse_allocation"
          ),
          count: this.getStatusCount("warehouse_allocation"),
          value: "warehouse_allocation"
        }
      ];
    }
  },
  async mounted() {
    if (this.$route.query.search) {
      this.filters = this.parseFiltersFromQuery(this.$route.query.search);
      this.appliedFilters = JSON.parse(JSON.stringify(this.filters));
    }
    if (this.$route.query.orderOpen) {
      this.openOrder(this.$route.query.orderOpen);
    }
    if (this.$route.query.showFilters) {
      this.showFilters = this.$route.query.showFilters == "true";
    }
    // Pide la key de Typesense (o usa la de desarrollo) antes de la primera
    // carga, para que ésta ya vaya a la fuente que el usuario dejó elegida.
    this.typesenseOrdersAvailable = await checkTypesenseOrdersAvailable();
    if (this.typesenseOrdersAvailable && readSavedSource() !== "mysql") {
      this.source = "typesense";
    }
    // En el siguiente tick: el watcher de source (que corre antes) ve
    // sourceReady=false y no repite la carga que se hace aquí.
    this.$nextTick(() => {
      this.sourceReady = true;
    });
    this.loadOrders();
    this.detectFocusOut();
    this.getStatus();
  },
  methods: {
    // La URL guarda los filtros aplanados (createQueryString): fromDate, seller,
    // etc. llegan al mismo nivel que page o search. Hay que devolverlos a
    // otherFilters; si quedan arriba, el query sale con la clave duplicada, el
    // backend se queda con la del nivel superior y el panel ya no puede
    // cambiar ni quitar esa fecha. Los tipos se toman de baseFilters para que
    // page no quede como string ("1" + 1 = "11").
    parseFiltersFromQuery(queryString) {
      const filters = JSON.parse(JSON.stringify(baseFilters));
      const coerce = (raw, defaultValue) => {
        if (typeof defaultValue === "boolean") return raw === "true";
        if (typeof defaultValue === "number") {
          const n = Number(raw);
          return Number.isFinite(n) && raw !== "" ? n : defaultValue;
        }
        return raw === "null" ? "" : raw;
      };
      // createQueryString convierte los espacios en "_"
      const spacedKeys = ["search", "shippingAddressName"];
      // URLSearchParams ya deshace el encodeURIComponent de cada valor
      for (const [key, raw] of new URLSearchParams(queryString)) {
        const value = spacedKeys.includes(key) ? raw.replace(/_/g, " ") : raw;

        if (key in baseFilters.otherFilters) {
          filters.otherFilters[key] = coerce(
            value,
            baseFilters.otherFilters[key]
          );
        } else if (key === "statusShipment") {
          filters.statusShipment = this.normalizeStatusShipment(value);
        } else if (key in baseFilters && key !== "otherFilters") {
          filters[key] = coerce(value, baseFilters[key]);
        }
      }
      if (filters.page < 1) filters.page = 1;
      this.ordersByPage = filters.limitPerPage;
      return filters;
    },
    getStatusCount(statusValue) {
      if (!this.generalStatus) {
        return null;
      }

      const statusKeyMap = this.getStatusKeyMapping();
      const apiStatusKey = Object.keys(statusKeyMap).find(key => {
        return statusKeyMap[key] === statusValue;
      });

      if (!apiStatusKey || this.generalStatus[apiStatusKey] === undefined) {
        return null;
      }

      return this.generalStatus[apiStatusKey];
    },
    normalizeStatusShipment(value) {
      if (value === null || value === undefined) {
        return value;
      }

      const normalizedValue = `${value}`.replace(/_/g, " ").trim();
      const matchedOption = this.orderStatusOptions.find(option => {
        return (
          `${option.value}`.toLowerCase() === normalizedValue.toLowerCase()
        );
      });

      return matchedOption ? matchedOption.value : value;
    },
    translateStatus(status) {
      if (!status) return "";
      const key = getOrderStatusKey(status);
      if (key) {
        const translationKey = `message.orders_page.order_statuses.${key}`;
        const translated = this.$t(translationKey);
        return translated !== translationKey ? translated : status;
      }
      return status;
    },
    getStatusKeyMapping() {
      return orderStatusMap;
    },
    getStatus() {
      getOrdersQtyByStatus().then(res => {
        this.generalStatus = res;
      });
    },
    showDraft() {
      this.openedOrder = null;
      this.showOrderModal = false;
      const newFilters = JSON.parse(JSON.stringify(baseFilters));
      this.$nextTick(() => {
        newFilters.statusShipment = "draft";
        this.filters = newFilters;
        this.applyFilters();
      });
    },
    onWindowFocusChange(e) {
      console.log("window focus event");
      if ({ focus: 1, pageshow: 1 }[e.type]) {
        if (this.windowIsFocused) return;
        this.windowIsFocused = true;
        this.loadOrders(true);
        this.getStatus();
      } else if (this.windowIsFocused) {
        this.windowIsFocused = false;
      }
    },
    detectFocusOut() {
      this.windowIsFocused = false;
      window.addEventListener("focus", this.onWindowFocusChange);
      window.addEventListener("blur", this.onWindowFocusChange);
      window.addEventListener("pageshow", this.onWindowFocusChange);
      window.addEventListener("pagehide", this.onWindowFocusChange);
    },
    applyFilters() {
      // Un filtro nuevo sobre la pagina 3 puede no tener pagina 3
      const withoutPage = f => JSON.stringify({ ...f, page: 0 });
      if (withoutPage(this.filters) !== withoutPage(this.appliedFilters)) {
        this.filters.page = 1;
      }
      this.appliedFilters = JSON.parse(JSON.stringify(this.filters));
    },
    applyOtherFilters() {
      this.applyFilters();
      this.showFilters = false;
    },
    resetFilters() {
      this.filters = JSON.parse(JSON.stringify(this.appliedFilters));
    },
    clearFilters() {
      this.filters.otherFilters = JSON.parse(
        JSON.stringify(baseFilters.otherFilters)
      );
      this.filtersKey++;
    },
    removeFilter(key) {
      if (key === "search") {
        this.filters.search = "";
        this.applyFilters();
        return;
      }
      const defaults = baseFilters.otherFilters;
      this.filters.otherFilters[key] = key in defaults ? defaults[key] : "";
      this.filtersKey++;
      this.applyFilters();
    },
    updateSearchFilter(search) {
      this.filters.page = 1;
      this.filters.search = search;
    },
    updateStatusFilter(status) {
      this.filters.page = 1;
      this.filters.statusShipment = status;
      if (status == "cancelled") {
        this.filters.otherFilters.hideCancelled = false;
      }
      this.showFilters = false;
      // Apply status filter changes immediately
      this.applyFilters();
    },
    updateStatusFilterMobile(status) {
      this.updateStatusFilter(status);
      this.showMobileMenu = false; // Close mobile menu after selection
    },
    async loadOrders(force = false) {
      this.apiCallId = new Date();
      // Las cotizaciones cargan su propia lista; descarta además cualquier
      // respuesta de órdenes que siga en vuelo.
      if (this.isQuotesSection) {
        this.loading = false;
        return;
      }
      let apiCallIdCurrent = this.apiCallId;
      const source = this.source;
      const started = performance.now();
      this.searchError = "";
      const request =
        source === "typesense"
          ? searchOrdersTypesense({
              filters: this.appliedFilters,
              departmentCode: getSelectedDepartmentCode()
            })
          : getOrders(this.appliedFilters, { skipCache: force });
      const res = await request.catch(error => {
        if (source === "typesense") {
          this.searchError = `Typesense: ${error.message}`;
        } else {
          alert("Error getting orders");
        }
      });
      if (apiCallIdCurrent !== this.apiCallId) {
        console.warn("new request in process");
        return;
      }
      if (!res) {
        this.orders = [];
        this.totalItems = 0;
        this.totalPages = 0;
        this.searchStats = "";
        this.loading = false;
        return;
      }
      // Tiempo visto desde el navegador (red incluida) y, en Typesense, el del
      // motor, para comparar las dos fuentes con la misma búsqueda.
      const roundTrip = Math.round(performance.now() - started);
      this.searchStats =
        source === "typesense"
          ? `Typesense: ${res.totalItems} results · ${roundTrip} ms (engine ${res.searchTimeMs} ms)`
          : `MySQL: ${res.totalItems} results · ${roundTrip} ms`;
      this.orders = res.orders;
      this.totalItems = res.totalItems;
      this.totalItems = res.totalItems;
      this.totalPages = res.totalPages;
      this.loading = false;
    },
    previous() {
      if (this.appliedFilters.page > 1) {
        this.appliedFilters.page = this.appliedFilters.page - 1;
        this.filters.page = this.appliedFilters.page;
      }
    },
    next() {
      if (this.appliedFilters.page < this.totalPages) {
        this.appliedFilters.page = this.appliedFilters.page + 1;
        this.filters.page = this.appliedFilters.page;
      }
    },
    showingFrom() {
      if (!this.orders.length) return 0;
      return (
        this.appliedFilters.limitPerPage * (this.appliedFilters.page - 1) + 1
      );
    },
    toQuantity() {
      return (
        this.appliedFilters.limitPerPage * (this.appliedFilters.page - 1) +
        this.orders.length
      );
    },
    changeArrowDir(headerValue) {
      this.filters.page = 1;
      this.appliedFilters.page = 1;
      if (headerValue == this.filters.orderCol) {
        this.filters.orderDir =
          this.filters.orderDir == "desc" ? "asc" : "desc";
      } else {
        this.filters.orderCol = headerValue;
        this.filters.orderDir = "desc";
      }
      // Apply sorting changes immediately
      this.applyFilters();
    },
    openOrder(orderNumber) {
      this.openedOrder = orderNumber;
      this.showOrderModal = true;
      const index = this.orders.findIndex(
        order => order.orderNumber == this.openedOrder
      );
      // Una cotización (o una orden fuera de la lista) no tiene vecinas
      if (index === -1) {
        this.hasNext = false;
        this.hasPrev = false;
        return;
      }
      if (index + 1 > this.orders.length - 1) {
        console.log("no tengo siguiente");
        this.hasNext = false;
      } else {
        console.log("xsi");
        this.hasNext = true;
      }

      // to do! = tengo previo?
      if (index - 1 < 0) {
        console.log("no tengo anterior");
        this.hasPrev = false;
      } else {
        console.log("si tengo anterior");
        this.hasPrev = true;
      }
    },
    exportCsv() {
      if (this.exporting) return;
      this.exporting = true;
      exportOrdersCsv(this.appliedFilters)
        .catch(() => {
          this.$toast.error("Error exporting orders");
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    // Lleva a la sección de cotizaciones con el formulario de nueva abierto
    newQuote() {
      this.updateStatusFilter(QUOTES_SECTION);
      this.$nextTick(() => {
        if (this.$refs.quotesList) this.$refs.quotesList.openNewForm();
      });
    },
    onOrderUpdated() {
      if (this.$refs.quotesList) {
        this.$refs.quotesList.reload();
      } else {
        this.loadOrders(true);
      }
    },
    openNextOrder() {
      const index = this.orders.findIndex(
        order => order.id == this.openedOrder
      );
      if (index + 1 > this.orders.length - 1) return;
      this.openOrder(this.orders[index + 1].orderNumber);
    },
    openPreviousOrder() {
      const index = this.orders.findIndex(
        order => order.id == this.openedOrder
      );
      if (index - 1 < 0) return;

      this.openOrder(this.orders[index - 1].orderNumber);
    }
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounce);
    console.log("remove event listeners");
    window.removeEventListener("focus", this.onWindowFocusChange);
    window.removeEventListener("blur", this.onWindowFocusChange);
    window.removeEventListener("pageshow", this.onWindowFocusChange);
    window.removeEventListener("pagehide", this.onWindowFocusChange);
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

// Mobile menu modal
.mobile-menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 60;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;

  @media (min-width: 1024px) {
    display: none;
  }

  .mobile-menu-content {
    background: white;
    width: 280px;
    max-width: 85vw;
    height: 100vh;
    overflow-y: auto;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);

    .mobile-menu-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      border-bottom: 1px solid #e5e7eb;
      background: #f9fafb;

      h3 {
        font-size: 18px;
        font-weight: 600;
        color: #111827;
        margin: 0;
      }

      .close-btn {
        padding: 4px;
        border: none;
        background: none;
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background: #e5e7eb;
        }

        i {
          font-size: 20px;
          color: #6b7280;
        }
      }
    }

    .left-menu {
      display: block !important;
      width: 100%;
      min-width: auto;
      box-shadow: none;
    }
  }
}

// Desktop sidebar: columna flex del ancho del menu, no un overlay absoluto con
// un margen fijo, para que al plegarlo el contenido recupere el espacio.
.desktop-sidebar {
  display: none;

  @media (min-width: 1024px) {
    display: flex;
    flex-shrink: 0;
    align-self: stretch;
  }
}

.content-main {
  position: relative;
  display: flex;
  min-height: 100vh;
  overflow-x: hidden;

  .content {
    flex: 1;
    min-width: 0;
  }

  .content {
    padding: 5px;
    width: 100%;

    .search-bar {
      background: white;
      padding: 15px;
      border-bottom: 1px solid #e5e7eb;
      display: flex;
      gap: 12px;
      align-items: center;
      flex-wrap: wrap;

      @media (max-width: 768px) {
        padding: 10px;
        flex-direction: column;
        align-items: stretch;
      }

      .search-wrapper {
        position: relative;
        flex: 1;
        min-width: 300px;

        @media (max-width: 768px) {
          min-width: 100%;
        }

        .search-icon {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #6b7280;
          font-size: 20px;
          pointer-events: none;
          z-index: 1;
        }

        .search-input {
          width: 100%;
          padding: 10px 44px 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 15px;
          transition: border-color 0.2s, box-shadow 0.2s;

          @media (max-width: 768px) {
            font-size: 16px;
            padding: 12px 44px 12px 12px;
          }

          &:focus {
            outline: none;
            border-color: $link-color-1;
            box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.1);
          }

          &::placeholder {
            color: #9ca3af;
          }
        }

        .clear-search {
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          padding: 4px;
          cursor: pointer;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: background-color 0.2s;

          &:hover {
            background-color: #f3f4f6;
          }

          i {
            font-size: 18px;
          }
        }
      }

      .search-actions {
        display: flex;
        gap: 8px;

        @media (max-width: 768px) {
          width: 100%;
          justify-content: flex-end;
        }
      }
    }

    .source-switch {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      user-select: none;
      font-size: 13px;
      color: #374151;

      input {
        position: absolute;
        opacity: 0;
        pointer-events: none;
      }

      .switch-track {
        position: relative;
        width: 36px;
        height: 20px;
        border-radius: 10px;
        background: #d1d5db;
        transition: background 0.15s;
      }

      .switch-thumb {
        position: absolute;
        top: 2px;
        left: 2px;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        background: white;
        transition: transform 0.15s;
      }

      input:checked + .switch-track {
        background: #111827;

        .switch-thumb {
          transform: translateX(16px);
        }
      }
    }

    .search-stats {
      width: 100%;
      font-size: 12px;
      color: #6b7280;

      &.is-error {
        color: red;
      }
    }

    .header {
      width: 100%;
      padding: 20px 15px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      border-bottom: 1px solid #e5e7eb;

      @media (max-width: 767px) {
        padding: 12px 8px;
        flex-wrap: wrap;
        gap: 12px;
      }

      .header-title {
        font-size: 20px;
        font-weight: bold;
        flex-shrink: 0;

        @media (max-width: 767px) {
          font-size: 18px;
          order: 1;
          width: 100%;
        }
      }

      .header-actions {
        display: flex;
        align-items: center;
        gap: 12px;
        flex-shrink: 0;

        @media (max-width: 767px) {
          order: 2;
          width: 100%;
          justify-content: space-between;
        }

        .mobile-actions {
          display: flex;
          align-items: center;
          gap: 12px;

          .mobile-menu-btn,
          .filter-btn {
            background: none;
            border: 1px solid #d1d5db;
            cursor: pointer;
            padding: 8px;
            border-radius: 6px;
            transition: background-color 0.2s;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            min-width: 44px;
            min-height: 44px;

            &:hover {
              background: rgba(0, 0, 0, 0.05);
            }

            i {
              color: #374151;
            }
          }

          // En escritorio el menu de estados ya esta en el sidebar
          .mobile-menu-btn {
            @media (min-width: 1024px) {
              display: none;
            }
          }

          .filter-btn {
            padding: 8px 12px;
            font-size: 14px;
            font-weight: 500;
            color: #374151;

            // No .active: _base-modal.vue lo fuerza a display: block global
            &.is-active {
              border-color: $link-color-1;
              color: $link-color-1;

              i {
                color: $link-color-1;
              }
            }

            &:disabled {
              opacity: 0.5;
              cursor: not-allowed;
            }

            .filter-btn-label {
              @media (max-width: 400px) {
                display: none;
              }
            }

            .filter-btn-count {
              min-width: 20px;
              height: 20px;
              padding: 0 6px;
              border-radius: 999px;
              background: $link-color-1;
              color: white;
              font-size: 12px;
              font-weight: 600;
              line-height: 20px;
            }
          }
        }

        .new-order-btn {
          white-space: nowrap;

          @media (max-width: 767px) {
            font-size: 14px;
            padding: 8px 16px;
          }
        }
      }
    }

    .no-results {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 60px 20px;
      text-align: center;
      color: #6b7280;

      .no-results-icon {
        font-size: 40px;
        color: #d1d5db;
      }

      p {
        font-size: 15px;
        margin: 0;

        strong {
          color: #374151;
        }
      }

      .btn-clear-button {
        margin-top: 4px;
      }
    }

    // Desktop Table
    .desktop-table {
      width: 100%;
      max-width: 100%;
      // App.scss le da calc(100% - 200px): con pocas filas estiraba el
      // contenedor hasta el fondo y dejaba la paginacion lejos de la tabla
      height: auto;
      overflow-x: auto;
      margin-bottom: 0;

      table {
        width: 100%;
        table-layout: fixed;

        th,
        td {
          font-size: 14px;
          padding: 8px 6px;
          overflow: hidden;
          text-overflow: ellipsis;
          text-align: left;
        }

        // Column widths - adjust based on content importance (12 columns)
        th:nth-child(1),
        td:nth-child(1) {
          width: 10%;
        } // Order Number
        th:nth-child(2),
        td:nth-child(2) {
          width: 10%;
        } // Date
        th:nth-child(3),
        td:nth-child(3) {
          width: 8%;
        } // Status
        th:nth-child(4),
        td:nth-child(4) {
          width: 10%;
        } // Products
        th:nth-child(5),
        td:nth-child(5) {
          width: 10%;
        } // Shipping Name
        th:nth-child(6),
        td:nth-child(6) {
          width: 7%;
        } // State
        th:nth-child(7),
        td:nth-child(7) {
          width: 7%;
        } // Country
        th:nth-child(8),
        td:nth-child(8) {
          width: 8%;
        } // Created By
        th:nth-child(9),
        td:nth-child(9) {
          width: 7%;
        } // Total
        th:nth-child(10),
        td:nth-child(10) {
          width: 9%;
        } // Shipping Pref
        th:nth-child(11),
        td:nth-child(11) {
          width: 4%;
        } // VIN
        th:nth-child(12),
        td:nth-child(12) {
          width: 10%;
        } // Seller

        // Hide Created By (8) and VIN (11) on tablet
        @media (max-width: 1023px) {
          th:nth-child(8),
          td:nth-child(8),
          th:nth-child(11),
          td:nth-child(11) {
            display: none;
          }

          // Redistribute widths on tablet (10 columns visible)
          th:nth-child(1),
          td:nth-child(1) {
            width: 12%;
          } // Order Number
          th:nth-child(2),
          td:nth-child(2) {
            width: 11%;
          } // Date
          th:nth-child(3),
          td:nth-child(3) {
            width: 9%;
          } // Status
          th:nth-child(4),
          td:nth-child(4) {
            width: 12%;
          } // Products
          th:nth-child(5),
          td:nth-child(5) {
            width: 12%;
          } // Shipping Name
          th:nth-child(6),
          td:nth-child(6) {
            width: 8%;
          } // State
          th:nth-child(7),
          td:nth-child(7) {
            width: 8%;
          } // Country
          th:nth-child(9),
          td:nth-child(9) {
            width: 8%;
          } // Total
          th:nth-child(10),
          td:nth-child(10) {
            width: 10%;
          } // Shipping Pref
          th:nth-child(12),
          td:nth-child(12) {
            width: 10%;
          } // Seller
        }
      }

      @media (max-width: 767px) {
        display: none !important;
      }
    }

    // Force hide table on mobile
    .table {
      @media (max-width: 767px) {
        display: none !important;
      }
    }

    // Mobile Cards
    .mobile-cards {
      display: none;

      @media (max-width: 767px) {
        display: block;
        padding: 8px 0;
      }

      @media (max-width: 390px) {
        padding: 4px 0;
      }

      .order-card {
        background: white;
        border: 1px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 12px;
        padding: 16px;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

        &:hover {
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
          transform: translateY(-1px);
        }

        &.selected {
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
        }

        &.warning {
          border-left: 4px solid #f59e0b;
          background-color: #fffbeb;
        }

        &.processed {
          border-left: 4px solid #10b981;
          background-color: #f0fdf4;
        }

        @media (max-width: 390px) {
          margin-bottom: 8px;
          padding: 12px;
          border-radius: 6px;
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid #f3f4f6;

          .order-number {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;

            i {
              font-size: 18px;
              flex-shrink: 0;
            }

            span {
              font-size: 16px;
              line-height: 1.4;
            }

            @media (max-width: 390px) {
              gap: 6px;

              i {
                font-size: 16px;
              }

              span {
                font-size: 14px;
              }
            }
          }

          .order-total {
            font-size: 16px;
            font-weight: bold;

            @media (max-width: 390px) {
              font-size: 14px;
            }
          }
        }

        .card-content {
          .info-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;
            font-size: 14px;
            color: #374151;

            i {
              font-size: 16px;
              width: 16px;
              flex-shrink: 0;
            }

            span {
              flex: 1;
              min-width: 0;

              &.truncate {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }

            @media (max-width: 390px) {
              gap: 6px;
              margin-bottom: 6px;
              font-size: 13px;

              i {
                font-size: 14px;
                width: 14px;
              }
            }
          }
        }

        .card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 12px;
          padding-top: 8px;
          border-top: 1px solid #f3f4f6;

          .status-indicators {
            display: flex;
            gap: 8px;

            .vin-indicator,
            .payment-warning,
            .processed-indicator {
              i {
                font-size: 16px;
              }
            }

            @media (max-width: 390px) {
              gap: 6px;

              .vin-indicator,
              .payment-warning,
              .processed-indicator {
                i {
                  font-size: 14px;
                }
              }
            }
          }

          .card-action {
            i {
              font-size: 20px;
              transition: transform 0.2s ease;
            }
          }
        }

        &:hover .card-footer .card-action i {
          transform: translateX(2px);
        }
      }
    }

    .table-footer {
      position: sticky;
      bottom: 0;
      z-index: 2;
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px 16px;
      padding: 8px 12px;
      background: #f9fafb;
      border-top: 1px solid #e5e7eb;
      box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.04);
      font-size: 14px;
      color: #4b5563;

      @media (max-width: 767px) {
        flex-direction: column;
        align-items: stretch;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 10px 12px;
      }

      .totals {
        strong {
          color: #111827;
          font-weight: 600;
        }

        @media (max-width: 767px) {
          text-align: center;
        }
      }

      .footer-controls {
        display: flex;
        align-items: center;
        gap: 16px;

        @media (max-width: 767px) {
          justify-content: space-between;
          gap: 8px;
        }
      }

      .per-page {
        display: flex;
        align-items: center;
        gap: 6px;
        white-space: nowrap;

        select {
          height: 32px;
          padding: 0 6px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          background: white;
          color: #111827;
        }
      }

      .pager {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .pager-page {
        min-width: 80px;
        text-align: center;
        white-space: nowrap;
        color: #111827;
      }

      .pager-btn {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        height: 32px;
        padding: 0 10px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        background: white;
        color: $link-color-1;
        font-weight: 500;
        transition: background-color 0.15s;

        i {
          font-size: 18px;
        }

        &:hover:not(:disabled) {
          background: rgba(18, 110, 106, 0.06);
          border-color: $link-color-1;
        }

        &:disabled {
          color: #9ca3af;
          cursor: not-allowed;
        }

        // En pantallas chicas solo las flechas
        @media (max-width: 400px) {
          padding: 0 6px;

          span {
            display: none;
          }
        }
      }
    }
  }

  // Payment warning badge
  .payment-warning-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: #f59e0b;
    border-radius: 50%;
    color: white;
    flex-shrink: 0;
    animation: pulse-warning 2s ease-in-out infinite;

    i {
      font-size: 16px;
      line-height: 1;
    }
  }

  @keyframes pulse-warning {
    0%,
    100% {
      box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
    }
    50% {
      box-shadow: 0 0 0 6px rgba(245, 158, 11, 0);
    }
  }

  // Responsive adjustments for filters and status buttons
  @media (max-width: 767px) {
    .btn-info-button,
    .btn-dark-button {
      font-size: 14px;
      padding: 8px 12px;
      margin: 4px;
    }
  }

  @media (max-width: 390px) {
    .btn-info-button,
    .btn-dark-button {
      font-size: 12px;
      padding: 6px 10px;
      margin: 2px;
    }
  }
}
.active-filters-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 12px;
}

.filter-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: #e8f0fe;
  color: #1a56db;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
}

.filter-badge-remove {
  display: inline-flex;
  align-items: center;
  color: #1a56db;
  opacity: 0.7;
  line-height: 1;

  &:hover {
    opacity: 1;
  }
}
</style>
